import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Search, Trees } from 'lucide-react';
import { CEMETERIES, formatLocation, formatMoney } from '../data/cemeteries';
import { getAvailability } from '../lib/plotStore';
import { useInventory, useServerInventory } from '../lib/useStore';

/**
 * Cemetery search. Matches on name or on any part of the location — city,
 * state, ZIP, county, or the full formatted address — so "Upper Marlboro",
 * "20772", "MD" and "Trinity" all find the same grounds.
 */
const matches = (cemetery, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const { street, city, state, zip, county } = cemetery.address;
  return [cemetery.name, cemetery.tagline, street, city, state, zip, county]
    .join(' ')
    .toLowerCase()
    .includes(q);
};

const Cemeteries = () => {
  useInventory();
  useServerInventory();
  const [query, setQuery] = useState('');

  const results = useMemo(() => CEMETERIES.filter((c) => matches(c, query)), [query]);

  return (
    <div className="bg-slate-50">
      {/* Header */}
      <section className="relative overflow-hidden bg-navy-gradient py-20 text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            background:
              'radial-gradient(55% 75% at 50% 0%, rgba(78,143,233,0.35) 0%, rgba(8,45,104,0) 70%)',
          }}
        />
        <div className="container relative mx-auto px-4 text-center">
          <p className="eyebrow animate-fade-up text-navy-200">Explore our grounds</p>
          <h1 className="mt-3 animate-fade-up font-cormorant text-4xl text-white sm:text-5xl">
            Find a Cemetery
          </h1>
          <p className="mx-auto mt-4 max-w-2xl animate-fade-up font-montserrat text-sm leading-relaxed text-navy-100">
            Search the grounds we manage by name or by location. Every cemetery has an
            interactive site map showing which sites are available, and what each one costs.
          </p>

          <div className="mx-auto mt-8 max-w-xl animate-fade-up">
            <label className="relative block">
              <span className="sr-only">Search cemeteries by name or location</span>
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, city, state, or ZIP…"
                className="w-full rounded-xl border border-white/20 bg-white py-4 pl-12 pr-4 font-montserrat text-sm text-slate-800 shadow-card-hover placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-navy-400"
              />
            </label>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="container mx-auto px-4 py-16">
        <p className="mb-6 font-montserrat text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
          {results.length === 0
            ? 'No cemeteries found'
            : `${results.length} ${results.length === 1 ? 'cemetery' : 'cemeteries'}${
                query.trim() ? ` matching “${query.trim()}”` : ''
              }`}
        </p>

        {results.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-card">
            <span className="icon-chip mx-auto h-16 w-16">
              <Trees className="h-8 w-8" />
            </span>
            <h2 className="mt-5 font-cormorant text-2xl font-semibold text-navy-900">
              Nothing matched that search
            </h2>
            <p className="mx-auto mt-2 max-w-md font-montserrat text-sm text-slate-500">
              Try a city, a ZIP code, or a cemetery name. If you are looking for grounds we do
              not yet manage,{' '}
              <Link to="/contact" className="font-medium text-navy-700 underline underline-offset-2">
                get in touch
              </Link>{' '}
              and we will help you find them.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {results.map((cemetery) => (
              <CemeteryCard key={cemetery.id} cemetery={cemetery} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

const CemeteryCard = ({ cemetery }) => {
  const counts = getAvailability(cemetery);
  const available = counts.available + counts.selected;
  const lowestPrice = Math.min(cemetery.pricing.burial, cemetery.pricing.cremation);

  return (
    <Link
      to={`/cemeteries/${cemetery.id}`}
      className="group block overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-navy-200 hover:shadow-card-hover"
    >
      <div className="aspect-[16/9] overflow-hidden bg-slate-100">
        <img
          src={cemetery.images[0].src}
          alt={cemetery.images[0].alt}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />
      </div>

      <div className="p-6">
        <h2 className="font-cormorant text-3xl font-semibold text-navy-900">
          {cemetery.name}
        </h2>
        <p className="mt-1.5 flex items-center gap-1.5 text-sm text-slate-500">
          <MapPin className="h-4 w-4 shrink-0" />
          {formatLocation(cemetery)}
        </p>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">{cemetery.tagline}</p>

        <dl className="mt-5 grid grid-cols-3 gap-4 border-t border-slate-100 pt-5">
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-slate-500">Available</dt>
            <dd className="mt-1 font-cormorant text-2xl font-semibold text-navy-900">
              {available}
              <span className="text-sm font-normal text-slate-500"> / {counts.total}</span>
            </dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-slate-500">From</dt>
            <dd className="mt-1 font-cormorant text-2xl font-semibold text-navy-900">
              {formatMoney(lowestPrice)}
            </dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-widest text-slate-500">Established</dt>
            <dd className="mt-1 font-cormorant text-2xl font-semibold text-navy-900">
              {cemetery.established}
            </dd>
          </div>
        </dl>

        <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-navy-800 group-hover:gap-2.5 transition-all">
          View grounds &amp; site map
          <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </Link>
  );
};

export default Cemeteries;
